import * as XLSX from 'xlsx';

/**
 * Exports dashboard statistics to an Excel file
 * @param {Object} stats - The statistics object from the API
 * @param {Array} expenses - The expenses array
 * @param {string} period - The selected period (today, yesterday, week, month, custom)
 * @param {Function} t - Translation function
 * @param {Object} customRange - Optional custom date range {startDate, endDate}
 */
export const exportStatsToExcel = (stats, expenses, period, t, customRange) => {
  if (!stats) return;

  const wb = XLSX.utils.book_new();
  const dateStr = new Date().toISOString().split('T')[0];

  let periodLabel = t(`dashboard.periods.${period}`);
  let filenameSuffix = period;

  if (period === 'custom' && customRange?.startDate && customRange?.endDate) {
    const start = customRange.startDate.toLocaleDateString();
    const end = customRange.endDate.toLocaleDateString();
    periodLabel = `${start} - ${end}`;
    filenameSuffix = `custom_${customRange.startDate.toISOString().split('T')[0]}_${customRange.endDate.toISOString().split('T')[0]}`;
  }

  // Calculate financials for export
  const totalExpenses = (expenses || []).reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
  const netProfit = (stats.summary.totalRevenue || 0) - totalExpenses;

  // --- Sheet 1: Summary ---
  const summaryData = [
    [t('dashboard.title'), `(${periodLabel})`],
    [''],
    [`--- ${t('export.financialBalance')} ---`],
    [t('export.totalRevenue'), stats.summary.totalRevenue],
    [t('export.totalExpenses'), totalExpenses],
    [t('export.netProfit'), netProfit],
    [''],
    [`--- ${t('export.indicators')} ---`],
    [t('export.averageTicket'), stats.summary.averageTicket],
    [t('export.cancellationRate'), `${(stats.summary.cancellationRate || 0).toFixed(1)}%`],
    [''],
    [`--- ${t('export.operational')} ---`],
    [t('dashboard.totalOrders'), stats.summary.totalOrders],
    [''],
    [t('dashboard.revenueBreakdown')],
    [t('dashboard.subtotal'), stats.summary.totalSubtotal],
    [`${t('dashboard.serviceCharge')} (10%)`, stats.summary.totalServiceCharge],
    [`${t('dashboard.tax')} (21%)`, stats.summary.totalTax],
    [t('dashboard.total'), stats.summary.totalRevenue],
    [''],
    [t('dashboard.ordersByStatus')],
    [t('orders.status.pending'), stats.byStatus.pending],
    [t('orders.status.preparing'), stats.byStatus.preparing],
    [t('orders.status.ready'), stats.byStatus.ready],
    [t('orders.status.delivered'), stats.byStatus.delivered],
    [t('orders.status.cancelled'), stats.byStatus.cancelled],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, wsSummary, t('export.summary'));

  // --- Sheet 1.5: Expense Detail ---
  if (expenses && expenses.length > 0) {
    const expenseData = [
      [t('export.date'), t('export.description'), t('export.category'), t('export.amount')],
      ...expenses.map((e) => [
        e.date,
        e.description,
        e.categoryName || e.categoryId,
        parseFloat(e.amount),
      ]),
    ];
    // Add total at bottom
    expenseData.push(['', '', 'TOTAL', totalExpenses]);

    const wsExpenses = XLSX.utils.aoa_to_sheet(expenseData);
    XLSX.utils.book_append_sheet(wb, wsExpenses, t('export.expenseDetail'));
  }

  // --- Sheet 2: Top Products ---
  if (stats.topProducts && stats.topProducts.length > 0) {
    const productsData = [
      [t('export.ranking'), t('export.product'), t('export.quantity'), t('export.revenue')],
      ...stats.topProducts.map((p, index) => [index + 1, p.name, p.quantity, p.revenue]),
    ];
    const wsProducts = XLSX.utils.aoa_to_sheet(productsData);
    XLSX.utils.book_append_sheet(wb, wsProducts, t('export.topProducts'));
  }

  // --- Sheet 3: Sales by Table ---
  if (stats.byTable && stats.byTable.length > 0) {
    const tablesData = [
      [t('export.table'), t('export.orders'), t('export.revenue')],
      ...stats.byTable.map((table) => [table.table, table.orders, table.revenue]),
    ];
    const wsTables = XLSX.utils.aoa_to_sheet(tablesData);
    XLSX.utils.book_append_sheet(wb, wsTables, t('export.salesByTable'));
  }

  // --- Sheet: Sales by Type ---
  if (stats.byOrderType && stats.byOrderType.length > 0) {
    const typeLabels = {
      dine_in: t('export.dineIn'),
      takeaway: t('export.takeaway'),
      delivery: t('export.delivery'),
    };
    const typeData = [
      [t('export.orderType'), t('export.orders'), t('export.revenue')],
      ...stats.byOrderType.map((item) => [
        typeLabels[item.type] || item.type,
        item.orders,
        item.revenue,
      ]),
    ];
    const wsType = XLSX.utils.aoa_to_sheet(typeData);
    XLSX.utils.book_append_sheet(wb, wsType, t('export.salesByType'));
  }

  // --- Sheet 4: Sales by Hour ---
  if (stats.byHour && stats.byHour.length > 0) {
    const hourlyData = [
      [t('export.hour'), t('export.orders'), t('export.revenue')],
      ...stats.byHour.map((h) => [`${h.hour}:00`, h.orders, h.revenue]),
    ];
    const wsHourly = XLSX.utils.aoa_to_sheet(hourlyData);
    XLSX.utils.book_append_sheet(wb, wsHourly, t('export.salesByHour'));
  }

  // --- Sheet 5: Daily Trend (only if daily data exists) ---
  if (stats.byDay && stats.byDay.length > 1) {
    const dailyData = [
      [t('export.date'), t('export.orders'), t('export.revenue')],
      ...stats.byDay.map((d) => [d.date, d.orders, d.revenue]),
    ];
    const wsDaily = XLSX.utils.aoa_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(wb, wsDaily, t('export.dailyTrend'));
  }

  // Generate and download file
  XLSX.writeFile(wb, `stats_${filenameSuffix}_${dateStr}.xlsx`);
};
